import React, { useState, useEffect } from 'react';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import {
    Button,
    Table,
    Modal,
    Form,
    Input,
    InputNumber,
    Select,
    message,
    Upload,
    Space,
    Tag,
    Image,
    Typography,
    Popconfirm,
} from 'antd';
import {
    UploadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    PictureOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { ingredientService } from '../../services/ingredientService';
import { ALLOWED_PRODUCT_LOT_SIZES, getDefaultLotSize } from '../../utils/lotSize';

const { TextArea } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

const ProductManagement: React.FC = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState<any>(null);
    const [form] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [ingredients, setIngredients] = useState<any[]>([]);
    const [updatingLotId, setUpdatingLotId] = useState<string | null>(null);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
        fetchIngredients();
    }, []);

    const fetchProducts = async () => {
        try {
            const data = await productService.getAllProducts();
            const sortedProducts = [...data].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            setProducts(sortedProducts);
        } catch (error) {
            message.error('Erreur lors de la récupération des produits');
        }
    };

    const fetchCategories = async () => {
        try {
            const data = await categoryService.getAllCategories();
            setCategories(data);
        } catch (error) {
            message.error('Erreur lors de la récupération des catégories');
        }
    };

    const handleAddProduct = () => {
        setEditingProduct(null);
        form.resetFields();
        setFileList([]);
        setIsModalVisible(true);
    };

    const handleEditProduct = (product: any) => {
        setEditingProduct(product);
        const nutritional = product?.nutritionalInfo || {};
        form.setFieldsValue({
            ...product,
            nutritionalInfo: Object.keys(nutritional).length ? {
                calories: nutritional.calories,
                proteins: nutritional.proteins,
                carbohydrates: nutritional.carbohydrates,
                fats: nutritional.fats,
            } : undefined,
        });
        setFileList((product.images || []).map((url: string) => ({
            uid: url,
            name: url.split('/').pop(),
            status: 'done',
            url: url
        })));
        setIsModalVisible(true);
    };

    const fetchIngredients = async () => {
        try {
            const data = await ingredientService.getAllIngredients();
            setIngredients(data);
        } catch (error) {
            message.error('Erreur lors de la récupération des ingrédients');
        }
    };

    const handleLotSizeChange = async (product: any, value: number | 'auto') => {
        const lotSize = value === 'auto' ? null : Number(value);
        setUpdatingLotId(product._id);
        try {
            const updated = await productService.updateProduct(product._id, { lotSize });
            setProducts(prev => prev.map(item => item._id === product._id ? { ...item, ...updated } : item));
            message.success(
                lotSize
                    ? `Lot de ${lotSize} pièces enregistré pour ${product.name}`
                    : `Lot automatique appliqué pour ${product.name}`
            );
        } catch (error) {
            message.error('Impossible de modifier le lot');
        } finally {
            setUpdatingLotId(null);
        }
    };

    const handleDeleteProduct = async (id: string) => {
        try {
            await productService.deleteProduct(id);
            message.success('Produit supprimé avec succès');
            fetchProducts();
        } catch (error) {
            message.error('Erreur lors de la suppression du produit');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const nutritional = values.nutritionalInfo || {};
            const hasNutritional = ['calories', 'proteins', 'carbohydrates', 'fats'].some(k => nutritional?.[k] !== undefined && nutritional?.[k] !== null && nutritional?.[k] !== '');
            const productData: any = {
                ...values,
                images: fileList.map(file => file.url || file.response?.url),
                lotSize: values.lotSize || null
            };
            if (hasNutritional) {
                productData.nutritionalInfo = {
                    calories: nutritional.calories,
                    proteins: nutritional.proteins,
                    carbohydrates: nutritional.carbohydrates,
                    fats: nutritional.fats,
                };
            } else {
                delete productData.nutritionalInfo;
            }

            if (editingProduct) {
                await productService.updateProduct(editingProduct._id, productData);
                message.success('Produit mis à jour avec succès');
            } else {
                await productService.createProduct(productData);
                message.success('Produit créé avec succès');
            }
            setIsModalVisible(false);
            fetchProducts();
        } catch (error) {
            message.error('Erreur lors de la sauvegarde du produit');
        }
    };

    const handleUpload = async (options: any) => {
        const { file, onSuccess, onError } = options;
        setUploading(true);

        const formData = new FormData();
        formData.append('image', file);
        try {
            const response = await axios.post('https://hellogassy-backend.onrender.com/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            const imageUrl = `${response.data.imageUrl}`;
            onSuccess(imageUrl);
            setFileList(prev => [...prev, {
                uid: imageUrl,
                name: file.name,
                status: 'done',
                url: imageUrl
            }]);
        } catch (error) {
            onError('Erreur lors de l\'upload de l\'image');
            message.error('Erreur lors de l\'upload de l\'image');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = (file: any) => {
        setFileList(prev => prev.filter(f => f.uid !== file.uid));
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesCategory = !categoryFilter || product.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const stockTag = (stock: number) => {
        if (stock <= 0) return <Tag color="red">Rupture</Tag>;
        if (stock <= 10) return <Tag color="orange">{stock} en stock</Tag>;
        return <Tag color="green">{stock} en stock</Tag>;
    };

    const columns = [
        {
            title: 'Produit',
            key: 'product',
            render: (_: any, record: any) => {
                const photo = record.images?.[0];
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 220 }}>
                        {photo ? (
                            <Image
                                src={photo}
                                alt={record.name}
                                width={64}
                                height={64}
                                style={{
                                    objectFit: 'cover',
                                    borderRadius: 12,
                                    flexShrink: 0,
                                }}
                                preview={{ mask: 'Voir' }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: 12,
                                    background: '#f5f0eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#c4b5a5',
                                    flexShrink: 0,
                                }}
                            >
                                <PictureOutlined style={{ fontSize: 22 }} />
                            </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, color: '#1a1412', lineHeight: 1.3 }}>
                                {record.name}
                            </div>
                            {record.description && (
                                <Text
                                    type="secondary"
                                    ellipsis
                                    style={{ display: 'block', fontSize: 12, maxWidth: 280 }}
                                >
                                    {record.description}
                                </Text>
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Catégorie',
            dataIndex: 'category',
            key: 'category',
            render: (category: string) => (
                <Tag style={{ borderRadius: 20, margin: 0 }} color="geekblue">
                    {category}
                </Tag>
            ),
        },
        {
            title: 'Prix',
            dataIndex: 'price',
            key: 'price',
            render: (price: number) => (
                <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {price.toLocaleString('fr-FR')} F
                </span>
            ),
        },
        {
            title: 'Lot',
            key: 'lotSize',
            width: 190,
            render: (_: any, record: any) => {
                const autoSize = getDefaultLotSize(record.price);
                const currentValue = record.lotSize || 'auto';
                return (
                    <Select
                        value={currentValue}
                        style={{ width: 170 }}
                        loading={updatingLotId === record._id}
                        disabled={updatingLotId === record._id}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(value) => handleLotSizeChange(record, value)}
                    >
                        <Option value="auto">
                            Auto{autoSize > 1 ? ` (${autoSize})` : ' (unité)'}
                        </Option>
                        {ALLOWED_PRODUCT_LOT_SIZES.map((size) => (
                            <Option key={size} value={size}>
                                Lot de {size}
                            </Option>
                        ))}
                    </Select>
                );
            },
        },
        {
            title: 'Stock',
            dataIndex: 'stock',
            key: 'stock',
            render: (stock: number) => stockTag(stock),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 200,
            render: (_: any, record: any) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEditProduct(record)}
                    >
                        Modifier
                    </Button>
                    <Popconfirm
                        title="Supprimer ce produit ?"
                        description="Cette action est définitive."
                        okText="Supprimer"
                        cancelText="Annuler"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDeleteProduct(record._id)}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />}>
                            Supprimer
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: 16,
                    marginBottom: 20,
                }}
            >
                <div>
                    <Title level={4} style={{ margin: 0, color: '#1a1412' }}>
                        Produits
                    </Title>
                    <Text type="secondary">
                        {filteredProducts.length} produit{filteredProducts.length > 1 ? 's' : ''}
                        {searchTerm || categoryFilter ? ' (filtrés)' : ''}
                    </Text>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddProduct}
                    size="large"
                    style={{ borderRadius: 10 }}
                >
                    Ajouter un produit
                </Button>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 12,
                    marginBottom: 16,
                    padding: '14px 16px',
                    background: '#faf8f6',
                    borderRadius: 12,
                    border: '1px solid #efe8e1',
                }}
            >
                <Input
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    allowClear
                    style={{ width: 280, maxWidth: '100%' }}
                />
                <Select
                    placeholder="Filtrer par catégorie"
                    value={categoryFilter || undefined}
                    onChange={value => setCategoryFilter(value || '')}
                    allowClear
                    style={{ width: 220 }}
                >
                    {categories.map((category: any) => (
                        <Option key={category._id} value={category.name}>{category.name}</Option>
                    ))}
                </Select>
            </div>

            <Table
                dataSource={filteredProducts}
                columns={columns}
                rowKey="_id"
                pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
                scroll={{ x: 720 }}
                rowClassName={() => 'hg-product-row'}
                locale={{ emptyText: 'Aucun produit trouvé' }}
            />

            <Modal
                title={editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                width={800}
                confirmLoading={uploading}
                okText={editingProduct ? 'Enregistrer' : 'Créer'}
                cancelText="Annuler"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Nom"
                        rules={[{ required: true, message: 'Veuillez entrer le nom du produit' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item name="description" label="Description courte">
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="detailedDescription"
                        label="Description détaillée"
                        rules={[{ required: true, message: 'Veuillez entrer la description détaillée' }]}
                    >
                        <TextArea rows={4} />
                    </Form.Item>

                    <Form.Item
                        name="price"
                        label="Prix"
                        rules={[{ required: true, message: 'Veuillez entrer le prix' }]}
                    >
                        <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="lotSize"
                        label="Taille du lot"
                        extra="Laisser vide pour garder la règle automatique (≤ 175 F = 25, > 175 F = 10, ≥ 1000 F = unité)."
                    >
                        <Select allowClear placeholder={`Automatique (ex. ${getDefaultLotSize(form.getFieldValue('price') || 0)} pièces)`}>
                            {ALLOWED_PRODUCT_LOT_SIZES.map((size) => (
                                <Option key={size} value={size}>
                                    Lot de {size}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="category"
                        label="Catégorie"
                        rules={[{ required: true, message: 'Veuillez sélectionner une catégorie' }]}
                    >
                        <Select>
                            {categories.map((category) => (
                                <Option key={category._id} value={category.name}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="ingredients"
                        label="Ingrédients"
                        rules={[{ required: true, message: 'Veuillez sélectionner au moins un ingrédient' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Sélectionnez les ingrédients"
                            optionFilterProp="children"
                            showSearch
                        >
                            {ingredients.map((ingredient: any) => (
                                <Option key={ingredient._id} value={ingredient.name}>
                                    {ingredient.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="preparationTime"
                        label="Temps de préparation"
                        rules={[{ required: true, message: 'Veuillez entrer le temps de préparation' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="allergens"
                        label="Allergènes"
                    >
                        <Select mode="tags" style={{ width: '100%' }} placeholder="Ajouter des allergènes" />
                    </Form.Item>

                    <Form.Item
                        name={['nutritionalInfo', 'calories']}
                        label="Calories"
                        rules={[]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name={['nutritionalInfo', 'proteins']}
                        label="Protéines (g)"
                        rules={[]}
                    >
                        <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name={['nutritionalInfo', 'carbohydrates']}
                        label="Glucides (g)"
                        rules={[]}
                    >
                        <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name={['nutritionalInfo', 'fats']}
                        label="Lipides (g)"
                        rules={[]}
                    >
                        <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        name="stock"
                        label="Stock"
                        rules={[{ required: true, message: 'Veuillez entrer la quantité en stock' }]}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item label="Images">
                        <Upload
                            customRequest={handleUpload}
                            fileList={fileList}
                            onRemove={handleRemove}
                            multiple
                            listType="picture-card"
                            accept="image/*"
                        >
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload</div>
                            </div>
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ProductManagement;
